import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import PageHead from "@/components/PageHead";
import { getBlogPostBySlug } from "@/lib/blog-posts";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useLocale } from "@/hooks/use-locale";

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const { prefix } = useLocale();
  const post = getBlogPostBySlug(slug || "");

  if (!post) {
    return (
      <section className="py-20 text-center">
        <h1 className="text-2xl font-bold mb-4">{t("blog.notFound")}</h1>
        <Button asChild variant="outline">
          <Link to={`${prefix}/blog`}>{t("blog.backToBlog")}</Link>
        </Button>
      </section>
    );
  }

  return (
    <>
      <PageHead
        title={`${post.title} — Queebi Blog`}
        description={post.excerpt}
        canonical={`https://queebi.com/blog/${post.slug}`}
        image={`https://queebi.com${post.image}`}
        ogType="article"
        keywords="Queebi, baby products, parenting tips, newborn essentials, pacifier, comforter, swaddle, emotional learning"
      />

      <article className="bg-background py-16 md:py-24">
        <div className="container max-w-3xl">
          <Link
            to={`${prefix}/blog`}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("blog.backToBlog")}
          </Link>

          <img src={post.image} alt={post.title} className="w-full rounded-2xl mb-8 aspect-[16/9] object-cover" width={800} height={450} />

          <p className="text-sm text-muted-foreground mb-2">
            {post.date} · {post.author}
          </p>
          <h1 className="text-3xl md:text-4xl font-bold font-heading mb-8 leading-tight">{post.title}</h1>

          <div className="prose prose-lg max-w-none prose-headings:font-heading prose-headings:text-foreground prose-p:text-foreground/80 prose-p:leading-relaxed prose-p:mb-6 prose-li:text-foreground/80 prose-strong:text-foreground prose-a:text-accent prose-a:underline">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>
          </div>

          <div className="mt-12 pt-8 border-t border-border">
            <Button asChild>
              <Link to={`${prefix}/blog`}>{t("blog.moreArticles")}</Link>
            </Button>
          </div>
        </div>
      </article>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: post.title,
            image: `https://queebi.com${post.image}`,
            datePublished: post.dateISO,
            author: { "@type": "Person", name: post.author },
            publisher: { "@type": "Organization", name: "Queebi Land" },
            description: post.excerpt,
            url: `https://queebi.com/blog/${post.slug}`,
          }),
        }}
      />
    </>
  );
};

export default BlogPost;
